import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { formatApiError } from "@/lib/format";

const STATUS_TONE = {
  pending: "bg-[#D4B878]/15 text-[#E8D6A3] border-[#D4B878]/30",
  reviewing: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function HQDashboard() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/hq/dashboard"),
      api.get("/franchise/applications"),
    ]);
    setStats(s.data);
    setApps(a.data);
  };
  useEffect(() => {
    load();
  }, []);

  const updateApp = async (id, status) => {
    try {
      await api.patch(`/franchise/applications/${id}`, { status, notes: "" });
      toast.success(`Marked ${status}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!stats) return <div className="text-zinc-500">Loading HQ dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="hq-dashboard">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
          HQ dashboard
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
          Pan-India operations.
        </h1>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <KPI label="Revenue" value={formatINR(stats.total_revenue)} tone="saffron" />
        <KPI label="Bookings" value={stats.total_bookings} />
        <KPI label="Hubs" value={stats.total_hubs} />
        <KPI label="Customers" value={stats.total_customers} />
        <KPI label="Active memberships" value={stats.active_memberships} />
        <KPI label="Pending franchise apps" value={stats.pending_franchise_apps} tone="saffron" />
      </div>

      <Tabs defaultValue="metrics">
        <TabsList className="bg-white/5 border border-white/10 rounded-full p-1">
          <TabsTrigger value="metrics" data-testid="hq-tab-metrics" className="rounded-full">
            Metrics
          </TabsTrigger>
          <TabsTrigger value="franchise" data-testid="hq-tab-franchise" className="rounded-full">
            Franchise pipeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-8 space-y-8">
          <ChartCard title="Revenue by hub">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.by_hub}>
                <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
                <XAxis dataKey="hub_name" stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
                <YAxis stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 12 }} />
                <Bar dataKey="revenue" fill="#D4B878" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Peak hours (bookings per hour)">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.peak_hours}>
                <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
                <XAxis dataKey="hour" stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
                <YAxis stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 12 }} />
                <Bar dataKey="count" fill="#E8D6A3" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </TabsContent>

        <TabsContent value="franchise" className="mt-8">
          <div className="space-y-3">
            {apps.length === 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500">
                No franchise applications yet.
              </div>
            )}
            {apps.map((a) => (
              <div
                key={a.id}
                data-testid={`franchise-app-${a.id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex items-start justify-between gap-4 flex-wrap"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className={`border ${STATUS_TONE[a.status] || ""} capitalize`}>
                      {a.status}
                    </Badge>
                    <span className="text-xs text-zinc-500">{a.format} · {a.investment_range}</span>
                  </div>
                  <div className="text-lg font-bold">{a.applicant_name}</div>
                  <div className="text-sm text-zinc-500">
                    {a.email} · {a.phone} · {a.city}, {a.state}
                  </div>
                  <div className="text-sm text-zinc-400 mt-1">
                    {a.profile} {a.message ? `· ${a.message}` : ""}
                  </div>
                </div>
                <div className="min-w-[180px]">
                  <Select defaultValue={a.status} onValueChange={(v) => updateApp(a.id, v)}>
                    <SelectTrigger
                      data-testid={`franchise-app-${a.id}-status`}
                      className="bg-white/5 border-white/10 rounded-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["pending", "reviewing", "approved", "rejected"].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ label, value, tone }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        tone === "saffron"
          ? "border-[#D4B878]/30 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="text-3xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">
        {title}
      </div>
      {children}
    </div>
  );
}
